import { Observable, firstValueFrom } from 'rxjs';

/** The slice of AuthService an SSE stream needs. */
export interface SseAuth {
  getToken(): string | null;
  refreshSession(): Observable<boolean>;
  handleUnauthorizedSession(): void;
}

export interface SseStreamOptions<T> {
  /** Resolved on every (re)connect, so query params and prefixes stay current. */
  url: () => string;
  /** Returns the parsed payload of one SSE frame, or null to skip it. */
  parseFrame: (frame: string) => T | null;
  initialDelayMs?: number;
  maxDelayMs?: number;
  backoffFactor?: number;
}

function hasUsableToken(token: string | null | undefined): token is string {
  return !!token && token !== 'null' && token !== 'undefined';
}

/**
 * Authenticated SSE over fetch (not EventSource, so the JWT goes in the Authorization header).
 * The raw fetch bypasses AuthTokenInterceptor, so the 401 handling is replicated here:
 *
 * - 401 → refreshSession(). If it renews, reconnect straight away with the new token.
 * - Logout (handleUnauthorizedSession) only when the refresh fails, or when the reconnect
 *   right after a successful refresh gets another 401 — that second case prevents a loop.
 * - Any successful connection clears the "just refreshed" state, so a later expiry can
 *   refresh again.
 *
 * Reconnects with exponential backoff on network errors and clean stream ends.
 */
export function createSseStream<T>(
  auth: SseAuth,
  options: SseStreamOptions<T>,
): Observable<T> {
  const initialDelayMs = options.initialDelayMs ?? 1000;
  const maxDelayMs = options.maxDelayMs ?? 30_000;
  const backoffFactor = options.backoffFactor ?? 2;

  return new Observable<T>((subscriber) => {
    let stopped = false;
    let abort: AbortController | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let delayMs = initialDelayMs;
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let justRefreshed = false;

    const scheduleReconnect = () => {
      if (stopped) return;
      reconnectTimer = setTimeout(() => {
        delayMs = Math.min(delayMs * backoffFactor, maxDelayMs);
        void connect();
      }, delayMs);
    };

    const giveUp = () => {
      stopped = true;
      subscriber.complete();
      auth.handleUnauthorizedSession();
    };

    const handleUnauthorized = async () => {
      if (justRefreshed) {
        giveUp();
        return;
      }
      let renewed = false;
      try {
        renewed = await firstValueFrom(auth.refreshSession(), { defaultValue: false });
      } catch {
        renewed = false;
      }
      if (stopped) return;
      if (!renewed) {
        giveUp();
        return;
      }
      justRefreshed = true;
      void connect();
    };

    const connect = async () => {
      if (stopped) return;
      abort = new AbortController();
      const token = auth.getToken();
      if (!hasUsableToken(token)) {
        scheduleReconnect();
        return;
      }
      try {
        const response = await fetch(options.url(), {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache',
          },
          cache: 'no-store',
          signal: abort.signal,
        });
        if (response.status === 401) {
          await handleUnauthorized();
          return;
        }
        if (!response.ok || !response.body) {
          throw new Error(`SSE failed: ${response.status}`);
        }
        justRefreshed = false;
        delayMs = initialDelayMs;
        reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (!stopped) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const frames = buffer.split('\n\n');
          buffer = frames.pop() ?? '';
          for (const frame of frames) {
            const payload = options.parseFrame(frame);
            if (payload !== null) {
              subscriber.next(payload);
            }
          }
        }
      } catch {
        if (stopped || abort?.signal.aborted) return;
      }
      scheduleReconnect();
    };

    void connect();

    return () => {
      stopped = true;
      abort?.abort();
      void reader?.cancel()?.catch(() => undefined);
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  });
}
